<template>
  <section ref="servicePanel" v-show="visible" class="web-customer-service" aria-label="在线咨询">
    <div class="web-customer-service__header">
      <div>
        <strong>在线咨询</strong>
        <span>客服回复将显示在这里</span>
      </div>
      <div class="web-customer-service__header-actions">
        <a-button type="link" size="small" title="收起咨询窗口" @click="minimize"><a-icon type="minus" /> 收起</a-button>
      </div>
    </div>

    <div v-if="!loggedIn" class="web-customer-service__login">
      <a-icon type="customer-service" />
      <h2>登录后即可在线咨询</h2>
      <p>登录后可发送文字、图片和文件。</p>
      <a-button type="primary" @click="openLogin">登录后咨询</a-button>
    </div>

    <template v-else>
      <div class="web-customer-service__chat">
        <div ref="messageBox" class="web-customer-service__messages">
          <div v-if="loading" class="web-customer-service__hint">会话加载中…</div>
          <template v-else-if="!messages.length">
            <div class="web-customer-service__empty">
              <a-icon type="message" />
              <p>您好，请描述您遇到的问题。</p>
              <p>需要查询时，直接发送完整保函编号或订单号即可。</p>
            </div>
          </template>
          <template v-else>
            <div v-if="hasMore" class="web-customer-service__more"><a-button type="link" :loading="loadingMore" @click="loadOlder">加载更早消息</a-button></div>
            <template v-for="item in messages">
              <div v-if="Number(item.id) === openedUnreadFirstMessageID" :key="`unread-${item.id}`" class="web-message__unread-divider">
                以下为 {{ openedUnreadCount }} 条新消息
              </div>
              <div :key="item.id" class="web-message" :class="`web-message--${item.direction}`">
              <div class="web-message__meta">{{ messageAuthor(item) }} · {{ formatTime(item.create_time) }}<span v-if="item.send_status === 'failed'"> · 发送失败</span></div>
              <img v-if="item.message_type === 'image' && mediaURLs[item.id]" class="web-message__image" :src="mediaURLs[item.id]" alt="聊天图片" @click="openMedia(item)" @error="refreshMedia(item)" />
              <div v-else-if="item.message_type === 'file' && mediaURLs[item.id]" class="web-message__file">
                <span>客服向您发送了文件</span>
                <strong>{{ fileDisplayName(item) }}</strong>
                <div class="web-message__file-actions">
                  <a :href="mediaURLs[item.id]" target="_blank" rel="noopener">查看文件</a>
                  <a href="javascript:void(0)" @click.prevent="downloadFile(item)">下载文件</a>
                </div>
              </div>
              <div v-else-if="sharedDocumentURL(item)" class="web-message__bubble web-message__bubble--document">
                <span>{{ sharedDocumentBefore(item) }}</span><a :href="sharedDocumentURL(item)" target="_blank" rel="noopener">打开文件</a><span>{{ sharedDocumentAfter(item) }}</span>
              </div>
              <div v-else class="web-message__bubble">{{ messageText(item) }}</div>
              </div>
            </template>
            <div ref="messageEnd" class="web-customer-service__message-end" aria-hidden="true"></div>
          </template>
        </div>

        <div class="web-customer-service__composer">
          <div v-if="conversation && conversation.status === 'closed'" class="web-customer-service__closed">本次会话已结束；发送新消息后将重新进入客服队列。</div>
          <div v-if="conversation && conversation.status !== 'closed' && conversation.service_mode === 'ai'" class="web-customer-service__ai-actions">
            <span v-if="conversation.ai_status === 'human_requested'" class="web-customer-service__handoff-waiting">已通知人工客服；接入前小保会继续帮您处理。</span>
            <a-button size="small" @click="aiAction('resolved')">结束咨询</a-button>
            <a-button size="small" :disabled="conversation.ai_status === 'human_requested'" @click="aiAction('handoff')">{{ conversation.ai_status === 'human_requested' ? '已通知人工' : '转人工' }}</a-button>
          </div>
          <div v-if="conversation && conversation.status !== 'closed' && conversation.service_mode === 'human'" class="web-customer-service__ai-actions">
            <span class="web-customer-service__handoff-waiting">人工客服正在处理；如暂时不需要帮助，您可随时结束本次咨询。</span>
            <a-button size="small" @click="aiAction('resolved')">结束咨询</a-button>
          </div>
          <input ref="imageInput" class="web-customer-service__file-input" type="file" accept="image/*" @change="sendMedia('image', $event)" />
          <input ref="fileInput" class="web-customer-service__file-input" type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt" @change="sendMedia('file', $event)" />
          <a-textarea v-model="content" :rows="3" :maxlength="1000" placeholder="请输入咨询内容" @keydown="handleKeydown" />
          <div class="web-customer-service__actions">
            <span>单个图片或文件不超过 10MB</span>
            <div><a-button :loading="uploading" @click="$refs.imageInput.click()">图片</a-button><a-button :loading="uploading" @click="$refs.fileInput.click()">文件</a-button><a-button type="primary" :loading="sending" @click="send">发送</a-button></div>
          </div>
        </div>
      </div>
    </template>
  </section>
</template>

<script>
  import { mapGetters, mapState } from 'vuex';
  import { externalCustomerServiceExchange, externalCustomerServiceUnreadRead, webCustomerServiceAIAction, webCustomerServiceConversation, webCustomerServiceMediaURL, webCustomerServiceSend, webCustomerServiceSendMedia } from '@/api/customer-service';
  import { requestUrl } from '@/config/app';

  export default {
    // 客服中心静态资源随官网版本一同构建发布；请勿只发布 API 后保留旧官网资源。
    name: 'WebCustomerService',
    props: { visible: { type: Boolean, default: true } },
    data() {
      return {
        conversation: null, messages: [], mediaURLs: {}, hasMore: false, nextBeforeID: 0,
        loading: false, loadingMore: false, sending: false, uploading: false, content: '',
        socket: null, reconnectTimer: null, destroyed: false,
        externalSession: null, externalBooting: false,
        lastNotificationAt: 0, notificationPromptBound: false,
        openedUnreadFirstMessageID: 0, openedUnreadCount: 0,
      };
    },
    computed: {
      ...mapState('user', ['userInfo']),
      ...mapGetters(['getToken']),
      externalTicket() { return String((this.$route.query || {}).external_ticket || '').trim(); },
      externalAccessToken() { return (this.externalSession || {}).access_token || ''; },
      externalClientCode() { return String((this.externalSession || {}).client || '').trim(); },
      activeUserID() { return Number((this.externalSession || {}).user_id || (this.userInfo || {}).id || 0); },
      loggedIn() { return !!this.externalAccessToken || (!!this.getToken && !!(this.userInfo || {}).id); },
      notificationSupported() { return typeof window !== 'undefined' && 'Notification' in window; },
    },
    watch: {
      getToken(value) {
        if (this.externalAccessToken) return;
        if (!value) this.resetConversation();
        else if ((this.userInfo || {}).id) this.initialize();
      },
      'userInfo.id'(value) {
        if (this.externalAccessToken) return;
        if (value && this.getToken) this.initialize();
        else if (!value) this.resetConversation();
      },
      externalTicket() { this.bootstrapExternalSession(); },
      visible(value) {
        if (value) { this.markAgentMessagesRead(); this.scrollToBottom(); this.bindNotificationRequest(); }
        else this.emitUnreadCount();
      },
    },
    mounted() {
      if (!this.externalTicket) {
        try {
          const saved = JSON.parse(window.sessionStorage.getItem('jdb-external-customer-service') || 'null');
          if (saved && saved.access_token && saved.user_id && saved.client && Date.parse(saved.expires_at || '') > Date.now()) this.externalSession = saved;
          else window.sessionStorage.removeItem('jdb-external-customer-service');
        } catch (_) { /* storage may be disabled or contain an old value */ }
      }
      this.bootstrapExternalSession();
      if (this.loggedIn) this.initialize();
      if (this.visible) this.bindNotificationRequest();
    },
    beforeDestroy() {
      this.destroyed = true;
      clearTimeout(this.reconnectTimer);
      if (this.socket) this.socket.close();
      this.unbindNotificationRequest();
    },
    methods: {
      async initialize() {
        if (!this.loggedIn) return;
        try {
          if (!this.externalAccessToken && !(this.userInfo || {}).id) await this.$store.dispatch('user/getUserInfoAction');
          await this.loadConversation();
          this.connectRealtime();
        } catch (_) {
          if (this.visible) this.$message.error('客服会话加载失败，请稍后重试');
        }
      },
      resetConversation() {
        clearTimeout(this.reconnectTimer);
        if (this.socket) this.socket.close();
        this.socket = null;
        this.conversation = null;
        this.messages = [];
        this.mediaURLs = {};
        this.$emit('unread-change', 0);
      },
      async bootstrapExternalSession() {
        const ticket = this.externalTicket;
        if (!ticket || this.externalBooting) return;
        this.externalBooting = true;
        this.resetConversation();
        try {
          const session = await externalCustomerServiceExchange(ticket);
          if (!session || !session.access_token || !session.user_id || !session.client) throw new Error('在线咨询链接无效');
          this.externalSession = session;
          try { window.sessionStorage.setItem('jdb-external-customer-service', JSON.stringify(session)); } catch (_) { /* private mode may reject storage */ }
          const query = { ...(this.$route.query || {}) };
          delete query.external_ticket;
          this.$router.replace({ path: this.$route.path, query });
          await this.initialize();
        } catch (error) {
          this.externalSession = null;
          this.$message.error(typeof error === 'string' ? error : '在线咨询链接已失效，请返回原平台重新进入');
        } finally { this.externalBooting = false; }
      },
      openLogin() {
        this.$store.commit('user/SET_LOGINSTATE', { state: 'PASSWORD' });
        this.$store.commit('user/SET_LOGINFLAG', true);
      },
      minimize() { this.$emit('minimize'); },
      // 外部机构通过一次性链接新开咨询页时，原页面与金电保域名不同，
      // 无法沿用入口点击的浏览器权限。用户首次操作咨询窗口时再申请，
      // 仍是浏览器认可的主动手势，不会在页面加载时强弹。
      bindNotificationRequest() {
        if (this.notificationPromptBound || !this.notificationSupported || window.Notification.permission !== 'default') return;
        this.$nextTick(() => {
          const panel = this.$refs.servicePanel;
          if (!panel || this.notificationPromptBound || window.Notification.permission !== 'default') return;
          panel.addEventListener('pointerdown', this.requestNotificationOnFirstInteraction);
          this.notificationPromptBound = true;
        });
      },
      unbindNotificationRequest() {
        const panel = this.$refs.servicePanel;
        if (panel) panel.removeEventListener('pointerdown', this.requestNotificationOnFirstInteraction);
        this.notificationPromptBound = false;
      },
      requestNotificationOnFirstInteraction() {
        if (!this.notificationSupported || window.Notification.permission !== 'default') {
          this.unbindNotificationRequest();
          return;
        }
        window.Notification.requestPermission()
          .then(() => {
            // 若用户只是关闭授权框，浏览器仍会是 default；保留监听，
            // 让其下次主动操作咨询窗口时可再次申请。
            if (window.Notification.permission !== 'default') this.unbindNotificationRequest();
          })
          .catch(() => { /* 浏览器可能保留 default，允许用户下次操作再尝试 */ });
      },
      async aiAction(action) {
        try { await webCustomerServiceAIAction(action, this.externalAccessToken); await this.loadConversation(); }
        catch (error) { this.$message.error((error && error.message) || '操作失败，请稍后重试'); }
      },
      unreadStorageKey() {
        const userID = this.activeUserID;
        if (!userID) return '';
        return this.externalClientCode
          ? `jdb-web-customer-service-read-external-${this.externalClientCode}-${userID}`
          : `jdb-web-customer-service-read-${userID}`;
      },
      lastReadMessageID() {
        const key = this.unreadStorageKey();
        if (!key) return 0;
        try { return Number(window.localStorage.getItem(key)) || 0; } catch (_) { return 0; }
      },
      setLastReadMessageID(id) {
        const key = this.unreadStorageKey();
        if (!key || !id) return;
        try { window.localStorage.setItem(key, String(id)); } catch (_) { /* storage may be disabled */ }
      },
      agentMessages() { return this.messages.filter((item) => item.direction === 'agent' && Number(item.sender_user_id) > 0); },
      markAgentMessagesRead(showUnreadMarker = false) {
        const unread = this.agentMessages().filter((item) => Number(item.id) > this.lastReadMessageID());
        if (showUnreadMarker && unread.length) {
          this.openedUnreadFirstMessageID = Number(unread[0].id) || 0;
          this.openedUnreadCount = unread.length;
        }
        const newest = this.agentMessages().reduce((max, item) => Math.max(max, Number(item.id) || 0), 0);
        if (newest) this.setLastReadMessageID(newest);
        this.$emit('unread-change', 0);
      },
      confirmExternalMessagesRead() {
        if (!this.externalAccessToken) return;
        // This runs only after the DOM has received the conversation result.
        // A transient acknowledgement failure leaves the homepage badge intact
        // and is retried on the next successful conversation load.
        externalCustomerServiceUnreadRead(this.externalAccessToken).catch(() => {});
      },
      emitUnreadCount() {
        const lastRead = this.lastReadMessageID();
        this.$emit('unread-change', this.agentMessages().filter((item) => Number(item.id) > lastRead).length);
      },
      async loadConversation(silent = false, beforeID = 0) {
        if (!this.loggedIn) return;
        if (beforeID) this.loadingMore = true;
        else this.loading = !silent;
        try {
          const result = await webCustomerServiceConversation(beforeID ? { before_id: beforeID } : {}, this.externalAccessToken);
          this.conversation = result.conversation || null;
          const incoming = result.messages || [];
          const known = new Set(this.messages.map((item) => item.id));
          const newlyReceived = silent && !beforeID ? incoming.filter((item) => !known.has(item.id)) : [];
          if (beforeID) this.messages = [...incoming, ...this.messages];
          else if (silent && this.messages.length) this.messages = [...this.messages, ...newlyReceived].sort((a, b) => a.id - b.id);
          else this.messages = incoming;
          this.hasMore = !!result.has_more;
          this.nextBeforeID = result.next_before_id || 0;
          await this.loadMediaURLs(incoming);
          if (!beforeID) this.scrollToBottom();
          if (this.visible) {
            this.markAgentMessagesRead(true);
            if (!beforeID) this.$nextTick(() => this.confirmExternalMessagesRead());
          } else this.emitUnreadCount();
          this.handleIncomingAgentMessages(newlyReceived);
        } finally {
          this.loading = false;
          this.loadingMore = false;
        }
      },
      async loadOlder() { if (this.nextBeforeID && !this.loadingMore) await this.loadConversation(false, this.nextBeforeID); },
      async loadMediaURLs(items) {
        const missing = (items || []).filter((item) => ['image', 'file'].includes(item.message_type) && item.media_ready && !this.mediaURLs[item.id]);
        if (!missing.length) return;
        const next = { ...this.mediaURLs };
        await Promise.all(missing.map(async (item) => {
          try { const result = await webCustomerServiceMediaURL(item.id, this.externalAccessToken); if (result.url) next[item.id] = result.url; } catch (_) { /* one expired link cannot block history */ }
        }));
        this.mediaURLs = next;
      },
      async refreshMedia(item) {
        const next = { ...this.mediaURLs };
        delete next[item.id];
        this.mediaURLs = next;
        await this.loadMediaURLs([item]);
      },
      async openMedia(item) {
        await this.refreshMedia(item);
        if (this.mediaURLs[item.id]) window.open(this.mediaURLs[item.id], '_blank', 'noopener');
      },
      messageAuthor(item) {
        if (item.direction === 'user') return '我';
        return Number(item.sender_user_id || 0) === 0 ? '小保' : '人工客服';
      },
      messageText(item) {
        if (item.message_type === 'image') return item.media_ready ? '图片加载中…' : '图片暂不可用';
        if (item.message_type === 'file') return item.media_ready ? '文件加载中…' : '文件暂不可用';
        return item.content;
      },
      fileDisplayName(item) { return String(item.content || '').replace(/^\[文件\]\s*/, '').trim() || '客服文件'; },
      downloadFile(item) {
        const url = this.mediaURLs[item.id];
        if (!url) return;
        const link = document.createElement('a');
        link.href = url;
        link.download = this.fileDisplayName(item);
        link.rel = 'noopener';
        link.click();
      },
      sharedDocumentURL(item) {
        if (item.direction !== 'agent' || item.message_type !== 'text') return '';
        const found = String(item.content || '').match(/https?:\/\/[^\s]+/);
        return found ? found[0] : '';
      },
      sharedDocumentBefore(item) {
        const link = this.sharedDocumentURL(item);
        return link ? String(item.content || '').split(link)[0].trim() : '';
      },
      sharedDocumentAfter(item) {
        const link = this.sharedDocumentURL(item);
        return link ? String(item.content || '').split(link).slice(1).join(link).trim() : '';
      },
      async send() {
        const content = this.content.trim();
        if (!content || this.sending) return;
        this.sending = true;
        try {
          await webCustomerServiceSend(content, this.externalAccessToken);
          this.content = '';
          await this.loadConversation(true);
        } catch (error) {
          this.$message.error(typeof error === 'string' ? error : '发送失败，请稍后重试');
        } finally { this.sending = false; }
      },
      async sendMedia(kind, event) {
        const input = event.target;
        const file = input.files && input.files[0];
        input.value = '';
        if (!file || this.uploading) return;
        if (file.size > 10 * 1024 * 1024) { this.$message.warning('文件不能超过 10MB'); return; }
        this.uploading = true;
        try {
          await webCustomerServiceSendMedia(kind, file, this.externalAccessToken);
          await this.loadConversation(true);
        } catch (error) {
          this.$message.error(typeof error === 'string' ? error : '发送失败，请稍后重试');
        } finally { this.uploading = false; }
      },
      handleKeydown(event) {
        if (event.key !== 'Enter' || event.shiftKey || event.isComposing || event.keyCode === 229) return;
        event.preventDefault();
        this.send();
      },
      scrollToBottom() {
        this.$nextTick(() => {
          const scroll = () => {
            const end = this.$refs.messageEnd;
            const box = this.$refs.messageBox;
            if (end && end.scrollIntoView) end.scrollIntoView({ block: 'end', behavior: 'auto' });
            else if (box) box.scrollTop = box.scrollHeight;
          };
          scroll();
          window.requestAnimationFrame(scroll);
          setTimeout(scroll, 80);
        });
      },
      formatTime(value) {
        if (!value) return '';
        const source = String(value).trim();
        const stamp = Date.parse(/(?:Z|[+-]\d{2}:?\d{2})$/i.test(source) ? source : `${source.replace(' ', 'T')}Z`);
        if (Number.isNaN(stamp)) return source;
        const date = new Date(stamp + 8 * 60 * 60 * 1000);
        const pad = (number) => String(number).padStart(2, '0');
        return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())} ${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}`;
      },
      handleIncomingAgentMessages(items) {
        const replies = (items || []).filter((item) => item.direction === 'agent' && Number(item.sender_user_id) > 0);
        if (!replies.length || (this.visible && !document.hidden)) return;
        this.showBrowserNotification();
      },
      showBrowserNotification() {
        if (!this.notificationSupported || window.Notification.permission !== 'granted') return;
        const now = Date.now();
        if (now - this.lastNotificationAt < 15000) return;
        this.lastNotificationAt = now;
        const notification = new window.Notification('电子保函系统', { body: '客服回复了您的咨询，点击查看' });
        notification.onclick = () => { window.focus(); notification.close(); this.$emit('request-open'); };
      },
      connectRealtime() {
        if (this.socket || !this.activeUserID || (!this.getToken && !this.externalAccessToken)) return;
        const api = String(requestUrl || window.location.origin).replace(/\/$/, '');
        const socketBase = api.replace(/^https:/, 'wss:').replace(/^http:/, 'ws:');
        const external = this.externalAccessToken;
        const socket = external
          ? new WebSocket(`${socketBase}/api/v1/external_customer_service/connect/${this.activeUserID}`, ['jdb-external', `external.${external}`])
          : new WebSocket(`${socketBase}/api/v1/web_customer_service/connect/${this.activeUserID}`, ['jdb-web', `bearer.${this.getToken}`]);
        this.socket = socket;
        socket.onmessage = (event) => {
          try { if (JSON.parse(event.data).type === 'customer_service_changed') this.loadConversation(true); } catch (_) { /* wake-up events have no message content */ }
        };
        socket.onclose = () => {
          this.socket = null;
          if (!this.destroyed && this.loggedIn) this.reconnectTimer = setTimeout(() => this.connectRealtime(), 3000);
        };
        socket.onerror = () => socket.close();
      },
    },
  };
</script>

<style lang="less" scoped>
  .web-customer-service { position:fixed; z-index:1001; right:24px; bottom:24px; display:flex; width:420px; height:min(680px, calc(100vh - 48px)); flex-direction:column; overflow:hidden; border:1px solid #dfe7f0; border-radius:12px; background:#fff; box-shadow:0 12px 36px rgba(30,53,80,.22); color:#34465c; }
  .web-customer-service__header { display:flex; min-height:56px; align-items:center; justify-content:space-between; padding:0 14px 0 18px; border-bottom:1px solid rgba(255,255,255,.18); background:#415695; color:#fff; }
  .web-customer-service__header strong { display:block; font-size:17px; }
  .web-customer-service__header span { display:block; margin-top:2px; color:rgba(255,255,255,.72); font-size:12px; }
  .web-customer-service__header-actions { display:flex; align-items:center; }
  .web-customer-service__header-actions .ant-btn { color:#fff; }
  .web-customer-service__header-actions .ant-btn[disabled] { color:rgba(255,255,255,.62); }
  .web-customer-service__login { display:flex; height:100%; flex:1; flex-direction:column; align-items:center; justify-content:center; padding:30px; text-align:center; }
  .web-customer-service__login .anticon { color:#415695; font-size:42px; }
  .web-customer-service__login h2 { margin:16px 0 8px; color:#21364e; font-size:19px; }
  .web-customer-service__login p { margin:0 0 20px; color:#76869a; font-size:13px; }
  .web-customer-service__chat { display:flex; min-height:0; flex:1; flex-direction:column; }
  .web-customer-service__messages { min-height:0; flex:1; overflow-y:auto; padding:18px; background:#f7f9fc; }
  .web-customer-service__message-end { height:1px; }
  .web-customer-service__empty,.web-customer-service__hint { padding-top:120px; color:#8b9aae; text-align:center; }
  .web-customer-service__empty .anticon { color:#9db2cf; font-size:36px; }
  .web-customer-service__empty p { margin:8px 0; font-size:13px; }
  .web-customer-service__more { text-align:center; }
  .web-message { display:flex; flex-direction:column; margin:0 0 14px; }
  .web-message--user { align-items:flex-end; }
  .web-message__unread-divider { display:flex; align-items:center; gap:8px; margin:4px 0 12px; color:#c34d4d; font-size:12px; }
  .web-message__unread-divider::before,.web-message__unread-divider::after { height:1px; flex:1; background:#f0caca; content:''; }
  .web-message__meta { margin-bottom:5px; color:#8b9aae; font-size:12px; }
  .web-message__bubble,.web-message__file { max-width:82%; border-radius:8px; padding:9px 12px; white-space:pre-wrap; word-break:break-word; }
  .web-message--user .web-message__bubble { background:#deefed; color:#31595a; }
  .web-message--agent .web-message__bubble { background:#fff; color:#34465c; box-shadow:0 1px 4px rgba(38,58,84,.08); }
  .web-message__bubble--document a { display:block; width:max-content; margin:8px 0; color:#245a9d; font-weight:600; }
  .web-message__file { display:flex; min-width:220px; flex-direction:column; gap:5px; }
  .web-message__file strong { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
  .web-message__file-actions { display:flex; gap:14px; margin-top:3px; }
  .web-message__file-actions a { color:#245a9d; font-size:13px; font-weight:600; text-decoration:none; }
  .web-message--user .web-message__file { background:#deefed; color:#31595a; }
  .web-message--agent .web-message__file { background:#fff; color:#34465c; box-shadow:0 1px 4px rgba(38,58,84,.08); }
  .web-message__image { max-width:260px; max-height:220px; cursor:zoom-in; border-radius:8px; }
  .web-customer-service__composer { padding:12px 14px; border-top:1px solid #e6edf4; background:#fff; }
  .web-customer-service__closed { margin-bottom:8px; color:#a67830; font-size:12px; }
  .web-customer-service__ai-actions { display:flex; align-items:center; gap:8px; margin-bottom:8px; color:#65768b; font-size:12px; }
  .web-customer-service__handoff-waiting { flex:1; color:#8a6500; }
  .web-customer-service__actions { display:flex; align-items:center; justify-content:space-between; margin-top:8px; color:#8b9aae; font-size:12px; }
  .web-customer-service__actions .ant-btn { margin-left:6px; }
  .web-customer-service__file-input { display:none; }
  @media (max-width: 640px) {
    .web-customer-service { top:0; right:0; bottom:0; width:100%; height:100%; border:0; border-radius:0; }
    .web-message__bubble,.web-message__file { max-width:88%; }
    .web-customer-service__actions { align-items:flex-end; flex-direction:column; gap:8px; }
  }
</style>
